import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, Download, ShieldAlert, ShieldCheck, XCircle } from 'lucide-react';
import { Badge, Button, Input, Modal, Spinner } from '@/components/ui';
import {
  downloadDocumentPdf,
  getDocument,
  issueDocument,
  verifyDocument,
  voidDocument,
  type VerificationResult,
} from '@/api/services/hrDocuments.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { formatLetterInstant, LETTER_STATUS } from '../letters/letterFormat';
import { saveBlob } from './documentFormat';

const SIGNER_STATUS: Record<string, string> = {
  pending: 'Waiting',
  viewed: 'Opened',
  signed: 'Signed',
  refused: 'Declined',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

/**
 * One generated document: its signers and progress, and the actions its
 * state allows (send for signature, withdraw, verify, download).
 */
export function DocumentDetailDialog({
  companyId,
  documentId,
  onClose,
}: {
  companyId: string;
  documentId: string;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [verification, setVerification] = useState<VerificationResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data: document, isLoading, error } = useQuery({
    queryKey: ['hr-document', companyId, documentId],
    queryFn: () => getDocument(companyId, documentId),
  });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hr-document', companyId, documentId] });
    void queryClient.invalidateQueries({ queryKey: ['hr-documents'] });
  };
  const fail = (mutationError: unknown): void => setFormError(getErrorMessage(mutationError));

  const issue = useMutation({
    mutationFn: () => issueDocument(companyId, documentId),
    onSuccess: () => {
      toast.success('Sent for signature');
      refresh();
    },
    onError: fail,
  });
  const withdraw = useMutation({
    mutationFn: () => voidDocument(companyId, documentId, reason.trim()),
    onSuccess: () => {
      toast.success('Document withdrawn');
      setVoiding(false);
      refresh();
    },
    onError: fail,
  });
  const verify = useMutation({
    mutationFn: () => verifyDocument(companyId, documentId),
    onSuccess: setVerification,
    onError: fail,
  });
  const download = useMutation({
    mutationFn: async (which: 'original' | 'completed') => {
      const blob = await downloadDocumentPdf(companyId, documentId, which);
      saveBlob(blob, `${document?.title ?? 'document'}${which === 'completed' ? ' (signed)' : ''}.pdf`);
    },
    onError: fail,
  });

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="flex justify-center py-10" role="status">
        <Spinner />
        <span className="sr-only">Loading document</span>
      </div>
    );
  } else if (error || !document) {
    body = (
      <p className="text-sm text-danger" role="alert">
        {getErrorMessage(error)}
      </p>
    );
  } else {
    const status = LETTER_STATUS[document.status];
    const signers = document.envelope?.signers ?? [];
    body = (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant}>{status.label}</Badge>
          {document.visibility === 'hr_only' && <Badge variant="neutral">HR only</Badge>}
          {document.containsCompensation && <Badge variant="warning">Contains pay</Badge>}
        </div>
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Employee">{document.employee?.name ?? document.resolvedVariables['employee.fullName'] ?? '—'}</Field>
          <Field label="Generated">{formatLetterInstant(document.createdAt)}</Field>
          <Field label="Sent">{formatLetterInstant(document.issuedAt)}</Field>
          {document.envelope?.expiresAt && document.status !== 'completed' && (
            <Field label="Sign by">{formatLetterInstant(document.envelope.expiresAt)}</Field>
          )}
          {document.completedAt && (
            <Field label="Completed">{formatLetterInstant(document.completedAt)}</Field>
          )}
          {document.voidReason && <Field label="Withdrawn because">{document.voidReason}</Field>}
        </dl>

        {signers.length > 0 && (
          <section aria-labelledby="signers-heading">
            <h3
              id="signers-heading"
              className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100"
            >
              Signers
            </h3>
            <ol className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
              {signers.map((signer) => (
                <li
                  key={signer.order}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                >
                  <div>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {signer.order}. {signer.displayName}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{signer.label}</p>
                    {signer.refusedReason && (
                      <p className="mt-1 text-xs text-gray-700 dark:text-gray-200">
                        Reason: {signer.refusedReason}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-gray-700 dark:text-gray-200">
                    {SIGNER_STATUS[signer.status] ?? signer.status}
                    {signer.signedAt && ` · ${formatLetterInstant(signer.signedAt)}`}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <section aria-labelledby="fingerprint-heading" className="space-y-2">
          <h3
            id="fingerprint-heading"
            className="text-sm font-semibold text-gray-900 dark:text-gray-100"
          >
            Integrity
          </h3>
          <p className="break-all font-mono text-xs text-gray-600 dark:text-gray-300">
            SHA-256 {document.artifact.sha256}
          </p>
          {verification && (
            <div
              role="status"
              className={
                verification.valid
                  ? 'rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900 dark:border-green-800 dark:bg-green-950/30 dark:text-green-100'
                  : 'rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100'
              }
            >
              <p className="flex items-center font-medium">
                {verification.valid ? (
                  <ShieldCheck className="mr-1.5 h-4 w-4" aria-hidden />
                ) : (
                  <ShieldAlert className="mr-1.5 h-4 w-4" aria-hidden />
                )}
                {verification.valid
                  ? 'Unchanged since it was generated and signed.'
                  : 'This document or its evidence has been altered.'}
              </p>
              <ul className="mt-2 space-y-1">
                {verification.checks.map((check) => (
                  <li key={check.check} className="flex items-center text-xs">
                    {check.valid ? (
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <XCircle className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    )}
                    {check.check}: {check.valid ? 'passed' : 'failed'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {voiding && (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (reason.trim().length >= 3) withdraw.mutate();
            }}
          >
            <Input
              id="void-reason"
              label="Why withdraw this document?"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setVoiding(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="danger"
                size="sm"
                disabled={reason.trim().length < 3}
                loading={withdraw.isPending}
              >
                Withdraw
              </Button>
            </div>
          </form>
        )}

        {formError && (
          <p className="text-sm text-danger" role="alert">
            {formError}
          </p>
        )}
      </div>
    );
  }

  const canVoid =
    document && ['generated', 'issued', 'partially_signed'].includes(document.status);
  const footer = document ? (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          loading={download.isPending && download.variables === 'original'}
          onClick={() => download.mutate('original')}
        >
          <Download className="mr-1.5 h-4 w-4" aria-hidden />
          PDF
        </Button>
        {document.completedArtifact && (
          <Button
            variant="ghost"
            size="sm"
            loading={download.isPending && download.variables === 'completed'}
            onClick={() => download.mutate('completed')}
          >
            <Download className="mr-1.5 h-4 w-4" aria-hidden />
            Signed copy
          </Button>
        )}
        <Button variant="ghost" size="sm" loading={verify.isPending} onClick={() => verify.mutate()}>
          Verify
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {canVoid && !voiding && (
          <Button variant="secondary" size="sm" onClick={() => setVoiding(true)}>
            Withdraw
          </Button>
        )}
        {document.status === 'generated' && (
          <Button size="sm" loading={issue.isPending} onClick={() => issue.mutate()}>
            Send for signature
          </Button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={document?.title ?? 'Document'}
      className="max-w-2xl"
      footer={footer}
    >
      {body}
    </Modal>
  );
}
